import React, { useState, useEffect } from 'react';
import { get } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');
const fmtDate = v => v ? new Date(v).toLocaleDateString('en-NG',{day:'numeric',month:'short',year:'numeric'}) : '—';

const DELIVERY_TYPES = {
  fez: { label:'Fez Logistics', color:'#3B5BDB', bg:'#E7F5FF' },
  public_transport: { label:'Public Transport', color:'#8B5E0A', bg:'#FFF3DC' },
  same_city_rider: { label:'Same City Rider', color:'#2E6B3A', bg:'#E8F5EB' },
  pickup: { label:'Pick Up', color:'#556B2F', bg:'#F2EFE7' },
};

const STATUS_COLORS = {
  pending: { color:'#8A8A8A', bg:'#F5F5F5', label:'Pending' },
  out_for_delivery: { color:'#8B5E0A', bg:'#FFF3DC', label:'Out for Delivery' },
  with_driver: { color:'#3B5BDB', bg:'#E7F5FF', label:'With Driver' },
  rider_picked_up: { color:'#2E6B3A', bg:'#E8F5EB', label:'Rider Picked Up' },
  arrived: { color:'#556B2F', bg:'#F2EFE7', label:'Arrived' },
  delivered: { color:'#2E6B3A', bg:'#D3F9D8', label:'Delivered' },
  picked_up: { color:'#2E6B3A', bg:'#D3F9D8', label:'Picked Up' },
};

export default function OperationsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    get('/deliveries/dashboard/stats')
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const Tile = ({label, value, icon, color, bg, sub, onClick}) => (
    <div onClick={onClick} style={{background:'#fff',borderRadius:12,padding:'18px 20px',
      boxShadow:'0 2px 8px rgba(0,0,0,.06)',borderLeft:'4px solid '+color,
      cursor:onClick?'pointer':'default'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
        <div>
          <div style={{fontSize:11,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>{label}</div>
          <div style={{fontSize:28,fontWeight:700,color:'#2C1F16'}}>{value}</div>
          {sub && <div style={{fontSize:11,color:'#8A8A8A',marginTop:4}}>{sub}</div>}
        </div>
        <div style={{fontSize:28}}>{icon}</div>
      </div>
    </div>
  );

  if (loading) return <div className="spinner" style={{marginTop:40}}/>;
  if (!data) return <div className="empty-state"><h3>Unable to load operations dashboard</h3></div>;

  const inTransit = (data.by_status.out_for_delivery||0) + (data.by_status.with_driver||0) +
    (data.by_status.rider_picked_up||0) + (data.by_status.arrived||0);
  const completed = (data.by_status.delivered||0) + (data.by_status.picked_up||0);

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',color:'var(--forest)'}}>
            🚚 Operations Dashboard
          </h2>
          <p className="text-muted text-sm">Delivery tracking and logistics overview</p>
        </div>
        <button className="btn btn-primary" onClick={()=>navigate('/deliveries')}>
          View All Deliveries
        </button>
      </div>

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24}}>
        <Tile label="Total Deliveries" value={data.total} icon="📦" color="#556B2F"
          onClick={()=>navigate('/deliveries')} />
        <Tile label="In Transit" value={inTransit} icon="🚚" color="#8B5E0A"
          sub="Active deliveries" onClick={()=>navigate('/deliveries?status=in_transit')} />
        <Tile label="Completed" value={completed} icon="✅" color="#2E6B3A"
          sub="Delivered & picked up" onClick={()=>navigate('/deliveries?status=completed')} />
        <Tile label="Pending" value={data.by_status.pending||0} icon="⏳" color="#8A8A8A"
          sub="Not yet dispatched" onClick={()=>navigate('/deliveries?status=pending')} />
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,marginBottom:24}}>
        <Tile label="Storage Fee Alerts" value={data.storage_fee_alerts.length} icon="⚠️" color="#8F2E24"
          sub={data.total_storage_fee > 0 ? fmt(data.total_storage_fee)+' accrued' : 'No fees accrued'}
          onClick={()=>navigate('/deliveries?status=storage_fee')} />
        <Tile label="Uncontacted Packages" value={data.uncontacted.length} icon="📞" color="#A8893D"
          sub="Customer not yet notified" onClick={()=>navigate('/deliveries?status=uncontacted')} />
        <Tile label="Fez Deliveries" value={data.by_type.fez||0} icon="🏎️" color="#3B5BDB"
          sub="Via Fez Logistics" onClick={()=>navigate('/deliveries?type=fez')} />
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:24}}>
        {/* Delivery by Type */}
        <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
          <div style={{fontWeight:700,fontSize:13,color:'#3B2A21',marginBottom:16}}>📊 Deliveries by Type</div>
          {Object.entries(DELIVERY_TYPES).map(([key,val])=>(
            <div key={key} style={{display:'flex',justifyContent:'space-between',alignItems:'center',
              padding:'8px 0',borderBottom:'1px solid #F2EFE7'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:11,padding:'3px 10px',borderRadius:12,fontWeight:600,
                  background:val.bg,color:val.color}}>{val.label}</span>
              </div>
              <strong style={{fontSize:14,color:'#2C1F16'}}>{data.by_type[key]||0}</strong>
            </div>
          ))}
        </div>

        {/* Delivery by Status */}
        <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
          <div style={{fontWeight:700,fontSize:13,color:'#3B2A21',marginBottom:16}}>📊 Deliveries by Status</div>
          {Object.entries(STATUS_COLORS).map(([key,val])=>(
            <div key={key} style={{display:'flex',justifyContent:'space-between',alignItems:'center',
              padding:'8px 0',borderBottom:'1px solid #F2EFE7'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:11,padding:'3px 10px',borderRadius:12,fontWeight:600,
                  background:val.bg,color:val.color}}>{val.label}</span>
              </div>
              <strong style={{fontSize:14,color:'#2C1F16'}}>{data.by_status[key]||0}</strong>
            </div>
          ))}
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        {/* Storage Fee Alerts */}
        <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
          <div style={{fontWeight:700,fontSize:13,color:'#8F2E24',marginBottom:16}}>⚠️ Storage Fee Alerts</div>
          {data.storage_fee_alerts.length === 0 ? (
            <p style={{color:'#8A8A8A',fontSize:13}}>No storage fees accruing</p>
          ) : data.storage_fee_alerts.map((d,i)=>(
            <div key={i} style={{padding:'8px 0',borderBottom:'1px solid #F2EFE7',fontSize:13,
              cursor:'pointer'}} onClick={()=>navigate('/deliveries')}>
              <div style={{display:'flex',justifyContent:'space-between'}}>
                <div>
                  <div style={{fontWeight:600,color:'#2C1F16'}}>{d.customer_name}</div>
                  <div style={{fontSize:11,color:'#8A8A8A',fontFamily:'monospace'}}>{d.tracking_code}</div>
                  <div style={{fontSize:11,color:'#8A8A8A'}}>{Math.floor(d.days_since_contact)} days since contact</div>
                </div>
                <div style={{color:'#8F2E24',fontWeight:700,fontSize:13}}>{fmt(d.storage_fee)}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Uncontacted Packages */}
        <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,.06)'}}>
          <div style={{fontWeight:700,fontSize:13,color:'#A8893D',marginBottom:16}}>📞 Uncontacted Packages</div>
          {data.uncontacted.length === 0 ? (
            <p style={{color:'#8A8A8A',fontSize:13}}>All customers have been contacted</p>
          ) : data.uncontacted.map((d,i)=>(
            <div key={i} style={{padding:'8px 0',borderBottom:'1px solid #F2EFE7',fontSize:13,
              cursor:'pointer'}} onClick={()=>navigate('/deliveries')}>
              <div style={{fontWeight:600,color:'#2C1F16'}}>{d.customer_name}</div>
              <div style={{fontSize:11,color:'#8A8A8A'}}>{d.customer_phone||'No phone'} · {DELIVERY_TYPES[d.delivery_type]?.label}</div>
              <div style={{fontSize:11,color:'#8A8A8A'}}>Created {fmtDate(d.created_at)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 2px 8px rgba(0,0,0,.06)',marginTop:16}}>
        <div style={{fontWeight:700,fontSize:13,color:'#3B2A21',marginBottom:16}}>🕐 Recent Delivery Activity</div>
        {data.recent.length === 0 ? (
          <p style={{color:'#8A8A8A',fontSize:13}}>No deliveries yet</p>
        ) : (
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'#F9F8F5'}}>
              <th style={{padding:'8px 12px',textAlign:'left',fontSize:11,color:'#8A8A8A',textTransform:'uppercase'}}>Tracking</th>
              <th style={{padding:'8px 12px',textAlign:'left',fontSize:11,color:'#8A8A8A',textTransform:'uppercase'}}>Customer</th>
              <th style={{padding:'8px 12px',textAlign:'left',fontSize:11,color:'#8A8A8A',textTransform:'uppercase'}}>Type</th>
              <th style={{padding:'8px 12px',textAlign:'left',fontSize:11,color:'#8A8A8A',textTransform:'uppercase'}}>Status</th>
              <th style={{padding:'8px 12px',textAlign:'left',fontSize:11,color:'#8A8A8A',textTransform:'uppercase'}}>Date</th>
            </tr></thead>
            <tbody>
              {data.recent.map((d,i)=>(
                <tr key={i} style={{cursor:'pointer',borderBottom:'1px solid #F2EFE7'}}
                  onClick={()=>navigate('/deliveries')}>
                  <td style={{padding:'8px 12px',fontFamily:'monospace',color:'#556B2F',fontWeight:600}}>{d.tracking_code}</td>
                  <td style={{padding:'8px 12px',fontWeight:500}}>{d.customer_name}</td>
                  <td style={{padding:'8px 12px'}}>
                    <span style={{fontSize:11,padding:'3px 8px',borderRadius:12,
                      background:DELIVERY_TYPES[d.delivery_type]?.bg,
                      color:DELIVERY_TYPES[d.delivery_type]?.color,fontWeight:600}}>
                      {DELIVERY_TYPES[d.delivery_type]?.label||d.delivery_type}
                    </span>
                  </td>
                  <td style={{padding:'8px 12px'}}>
                    <span style={{fontSize:11,padding:'3px 8px',borderRadius:12,
                      background:STATUS_COLORS[d.status]?.bg,
                      color:STATUS_COLORS[d.status]?.color,fontWeight:600}}>
                      {STATUS_COLORS[d.status]?.label||d.status}
                    </span>
                  </td>
                  <td style={{padding:'8px 12px',color:'#8A8A8A',fontSize:12}}>{fmtDate(d.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
